// libraries
import type { StateTree } from '@makemydeal/dr-dash-types';
import { ToggleUseSource, toggleUseRegistry } from '@makemydeal/dr-feature-toggles-client';
import { PAYMENT_GRID_BAGGAGE_COMPRESSION_TOGGLE, type FeatureToggles } from '@makemydeal/dr-platform-shared';

// utils
import { clientLogger } from '@makemydeal/dr-shared-ui-utils';
import { getFeatureToggleFromSlice } from '../reducers/featureToggleReducer';
// interfaces/types
import type { FeatureTogglesAndUrlOnly } from '../types';
// selectors
import { wasBootstrapSuccessful } from './sharedBootstrapSelectors';

export type FeatureTogglePreconditionCheckOptions = {
    throwError?: boolean;
    logMessage?: boolean;
};

const PRECONDITION_CHECK_DEFAULTS = { throwError: true };

/**
 * Every feature toggle selector should include this check.  By default this will throw an error if toggled code is used before
 * the app has been bootstrapped.  If there's a scenario where it is "safe" to avoid this you can override the behavior by using
 * the options argument.
 *
 * NOTE: "safe" scenarios are defined as code that can be executed regardless of the toggle state- this is rare because the most
 * common use for feature toggles is to prevent untoggled code from executing at all times.  One possible "safe" use-case is if the
 * UI checks the state initially and hides elements and then later shows elements but this doesn't cause undesirable flicker.
 *
 * IMPORTANT: Any "unsafe" feature toggle selectors (those that do not throw exceptions if they are used before bootstrap completes
 *   should have a name like `isXyzEnabledAfterBootstrap` instead of just `isXyzEnabled`).
 */
export const featureTogglePreconditionCheck = (
    state: StateTree,
    options: FeatureTogglePreconditionCheckOptions = PRECONDITION_CHECK_DEFAULTS
) => {
    if (!areFeatureTogglesAvailable(state)) {
        if (options.logMessage) {
            clientLogger.clientWarnMessage('Feature toggles retrieved before app has successfully bootstrapped.');
        }
        if (options.throwError) {
            throw new Error('Feature toggles retrieved before app has successfully bootstrapped.');
        }
    }
};

export const areFeatureTogglesAvailable = (state: StateTree) => {
    return wasBootstrapSuccessful(state);
};

/**
 * Returns feature toggles with the ability to set pre-condition checks to various levels defined by the options parameter.  If
 * omitted the default behavior will protect the calling code from using feature toggles prior to bootstrap completion.
 */
export const getFeatureToggles = (
    state: StateTree,
    options: FeatureTogglePreconditionCheckOptions = PRECONDITION_CHECK_DEFAULTS
): Partial<FeatureTogglesAndUrlOnly> => {
    featureTogglePreconditionCheck(state, options);
    return state.featureToggles || ({} as Partial<FeatureTogglesAndUrlOnly>);
};

/**
 * Similar to getFeatureToggles but converts feature toggles into a more loosely typed object keyed by the toggle name.  This is
 * useful when passing the feature toggles to some activities.
 *
 * NOTE: In future we will not support this- the feature toggles should not be passed directly into the activity, they should
 *   instead use feature toggle selectors and pass the toggles in via the activity's additionalSelectors.
 */
export const getSimpleFeatureToggles = (
    state: StateTree,
    options: FeatureTogglePreconditionCheckOptions = PRECONDITION_CHECK_DEFAULTS
): FeatureToggles => {
    const featureToggles = getFeatureToggles(state, options);
    return featureToggles as FeatureToggles;
};

/**
 * Returns feature toggle data without error before bootstrap has completed.  Using this incorrectly could result in UI flicker,
 * skipped business logic or race conditions.  Only use this if you're sure that the logic isn't prone to race conditions and will
 * handle the initial "false" toggle value that could change to "true" after bootstrap completion.
 */
export const getTogglesWithoutSafetyChecks = (state: StateTree): Partial<FeatureTogglesAndUrlOnly> => getFeatureToggles(state, {});

/**
 * To make it easier to port code from shopper platform this function is aliased.  See how `getFeatureToggles` is defined above
 * for more information.
 */
export const getFeatureToggleState = (state: StateTree): Partial<FeatureTogglesAndUrlOnly> => getFeatureToggles(state);

/**
 * Get a feature toggle by name in a type-safe way - this will ensure that the caller uses a feature toggle name that exists in the
 * FeatureTogglesAndUrlOnly type.
 */
export const getFeatureToggle = (
    state: StateTree,
    name: keyof FeatureTogglesAndUrlOnly,
    source: ToggleUseSource = ToggleUseSource.Selector
): boolean => {
    toggleUseRegistry.registerToggleUse(name, source);
    const featureToggles: FeatureTogglesAndUrlOnly = getFeatureToggleState(state);
    return getFeatureToggleFromSlice(featureToggles, name);
};

/**
 * NOTE: This should only be used by the useFeatureToggle hook.
 */
export const featureEnabledSelectorForHook = (name: keyof FeatureTogglesAndUrlOnly) => (state: StateTree) =>
    getFeatureToggle(state, name, ToggleUseSource.Hook);

/**
 * Per the 2026-07-22 Rally refinement of US1971722, the 8 previously-consolidated AI-desking feature
 * toggles below are now driven by a single dealer setting, `dealer.deskingIntelligenceSettings.enabled`,
 * instead of individual toggles. Read fail-closed: only `enabled === true` counts as on - absent, `{}`,
 * and `enabled: false` are all off (see the `Dealer['deskingIntelligenceSettings']` doc comment).
 *
 * This preserves the bootstrap precondition check that `getFeatureToggle` provided for these toggles, so
 * using them before the app has bootstrapped still throws loudly instead of silently resolving to `false`.
 * NOTE: unlike `getFeatureToggle`, this does not call `toggleUseRegistry.registerToggleUse` - that registry
 * tracks feature-toggle usage and has no meaning for a dealer setting.
 */
const isDeskingIntelligenceEnabled = (state: StateTree): boolean => {
    featureTogglePreconditionCheck(state);
    return state.dealer?.deskingIntelligenceSettings?.enabled === true;
};

export const isStripTradeInUIFieldsEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableStripTradeInUIFields');

export const isMenuUpdateWhenSdpTermsChangeEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'menuUpdateWhenSdpTermsChange');

export const showMenuPaymentError = (state: StateTree): boolean => getFeatureToggle(state, 'showMenuPaymentError');

export const isEnableLenderDecisionsInCreditBureau = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableLenderDecisionsInCreditBureau');

export const isShowDealPulseBadgeMVEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableShowDealPulseBadgeMV');

export const isEnableBuyerStateFromZip = (state: StateTree): boolean => getFeatureToggle(state, 'enableBuyerStateFromZip');

export const isEnableDealerCommentsEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableDealerComments');

export const isEnableBuyRateForCreditDecision = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableBuyRateForCreditDecision');

export const isDpmEnabledAfterBootstrap = (state: StateTree): boolean => getTogglesWithoutSafetyChecks(state).enableDpm || false;

export const isConsolidatedShareMenuEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F305203_Consolidate_Share_Menu');

export const isAmdShareLinkEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F302128_Amd_shareLink');

export const isAmdPdfWorkflowEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F302128_Amd_Pdf_Workflow');

export const isDealLifeCycleEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableDealLifeCycle');

export const isDealerTimeZoneForContractDateEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'useDealerTimeZoneForContractDate_DE752874');

export const isInterstateUiEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableInterstateUi');

export const isLenderLogosEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableLenderLogos');

export const getEnableAppliedLenderInfo = (state: StateTree): boolean => getFeatureToggle(state, 'enableAppliedLenderInfo');

export const isEnableCustomerCardNotifications = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableCustomerCardNotifications');

export const isEnhancedCustomerCardEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enhancedCustomerCard');

export const isReduxTelemetryEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableReduxTelemetry');

export const isStateSnapshotTelemetryEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableStateSnapshotTelemetry');

export const isBffTelemetryEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableBffTelemetry');

export const isPushToDmsSmartSaveEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'pushToDmsSmartSave');

export const usePaymentProductsMonthlyPayment = (state: StateTree): boolean =>
    getFeatureToggle(state, 'usePaymentProductsMonthlyPayment');

export const isPriorBalanceEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F300850_Add_Prior_Balance');

export const useCoOrgIdInVAutoAppraisalUrl = (state: StateTree): boolean =>
    getFeatureToggle(state, 'useCoOrgIdInVAutoAppraisalUrl');

export const enableHideRouteOneCreditBureau = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableHideRouteOneCreditBureau');

export const enableCreditBureauAndDecisionPBCRouteOne = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableCreditBureauAndDecisionPBCRouteOne');

export const enablePendoInMV = (state: StateTree): boolean => getFeatureToggle(state, 'enablePendoInMV');

export const disableReservationActivityLink = (state: StateTree): boolean =>
    getFeatureToggle(state, 'disableReservationActivityLink');

export const isContractErrorMappingEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableContractErrorMapping');

export const isMVSalesTeamSlideoutEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableMVSalesTeamSlideout');

export const enablePushToCUDL = (state: StateTree): boolean => getFeatureToggle(state, 'enablePushToCUDL');

export const enablePushToR1 = (state: StateTree): boolean => getFeatureToggle(state, 'enablePushToR1');

export const isGenericPushToFiForR1JEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F291912_Use_Generic_Push_To_FI_For_R1J');

export const enableOptionalPbcUi = (state: StateTree): boolean => getFeatureToggle(state, 'enableOptionalPbcUi');

export const isVppEditGuardEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableVppEditGuard');

export const isPushToDarwinEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enablePushToDarwin');

export const isSendToDealerTrackEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE768419_Enable_Send_To_Dealer_Track');

export const isCorrectPhoneHandlingEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE770255_Correct_Phone_Handling');

export const enableDigitalDealJacket = (state: StateTree): boolean => getFeatureToggle(state, 'enableDigitalDealJacket');

export const getEnhancedDealDetails = (state: StateTree): boolean => getFeatureToggle(state, 'enhancedDealDetails_F209893');

export const getEnableCustomLeaseMileageByDealer = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableCustomLeaseMileage_byDealer');

export const getEnableCustomLeaseMileageByFeature = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableCustomLeaseMileage_F209898');

export const isFordLincolnLendersEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2053560_Enable_Ford_Lincoln_Lenders');

export const isPIProgramDescEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'US2069163_PI_Slideout_Program_Desc');

export const isTargetOutOfPocketLeaseEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableTargetOutOfPocketLease');

export const isTotalTaxRateFieldEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableTotalTaxRateField');

export const useManualTotalTaxEdited = (state: StateTree): boolean => getFeatureToggle(state, 'useManualTotalTaxEdited');

export const enableDealSummaryLayoutV2 = (state: StateTree): boolean => getFeatureToggle(state, 'dealSummaryLayoutV2');

export const useFniMode = (state: StateTree): boolean => getFeatureToggle(state, 'useFniMode');

export const enableVinCrmPersonaFallback = (state: StateTree): boolean => getFeatureToggle(state, 'enableVinCrmPersonaFallback');

export const enableCommonConsumerPersonaFallback = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableCommonConsumerPersonaFallback');

export const enableDealerEmployeeSearch = (state: StateTree): boolean => getFeatureToggle(state, 'enableDealerEmployeeSearch');

export const enableDueAtSigningEnhancements = (state: StateTree): boolean =>
    getFeatureToggle(state, 'dueAtSigningEnhancements_E40674');

export const enableStatusLambdaSwitch = (state: StateTree): boolean => getFeatureToggle(state, 'docUploadStatusLambda');

export const useNewTermIdStandard = (state: StateTree): boolean => getFeatureToggle(state, 'useNewTermIdStandard');

export const enablePerpetualBypassShortIoForPdfService = (state: StateTree): boolean =>
    getFeatureToggle(state, 'perpetual_bypassShortIoForPdfService');

export const enableMVContractingSourceAppHeader = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableMVContractingSourceAppHeader');

export const isLenderComparisonMVEnabled = (state: StateTree): boolean => isDeskingIntelligenceEnabled(state);

export const isLenderComparisonWithProfitEnabled = (state: StateTree): boolean => isDeskingIntelligenceEnabled(state);

export const isRollToTabPersistenceEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableRollToTabPersistence');

export const isGetRollToCashDueEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableGetRollToCashDue');

export const enableNotificationSuppress = (state: StateTree): boolean => getFeatureToggle(state, 'enableNotificationSuppress');

export const isEquityAllocationOrderEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableEquityAllocationOrder');

export const isPorscheProRataEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enablePorscheProRata');

export const isReserveSplitOverrideEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F294383_enableReserveSplit');

export const isMvDealValidationEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'mvDealValidation');

export const useEnablePrintAuditLog = (state: StateTree): boolean => getFeatureToggle(state, 'enablePrintAuditLog');

export const useDealRecapEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableDealRecap');

export const isDealScreenRedDotErrorsEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'DealScreenRedDotErrors');

export const isFsPatchAndNameChangeErrorsEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'FsPatchAndNameChangeErrors');

export const removeCashOffsetEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'removeCashOffset');

export const enableOrderAgnosticILDPUI = (state: StateTree): boolean => getFeatureToggle(state, 'enableOrderAgnosticILDPUI');

export const isConvertPhoneNumberEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'convertPhoneNumber');
export const isEnableUpdatePendoMetadata = (state: StateTree): boolean => getFeatureToggle(state, 'enableUpdatePendoMetadata');

export const useLockFinalizedDeals = (state: StateTree): boolean => getFeatureToggle(state, 'lockFinalizedDeals');
export const isDisplayContractStatusEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'contractStatus_F22722');
export const useSoftPullIntegrationMV = (state: StateTree): boolean => getFeatureToggle(state, 'useSoftPullIntegrationMV');
export const enableOnePayLease_F231960 = (state: StateTree): boolean => getFeatureToggle(state, 'enableOnePayLease_F231960');

export const isEnableTradeTaxSavingsField = (state: StateTree): boolean => getFeatureToggle(state, 'enableTradeTaxSavingsField');
export const isEnableLeaseContractFields = (state: StateTree): boolean => getFeatureToggle(state, 'enableLeaseContractFields');
export const isAIAffordabilityIndicatorEnabled = (state: StateTree): boolean => isDeskingIntelligenceEnabled(state);
// Consolidated into the dealer setting with the other seven, per the 2026-07-22 Rally refinement of
// US1971722, which names all 8 toggles. Accepted consequence, recorded here deliberately: this selects
// which payload is sent to the BFF for AI calls (live unsaved offer vs saved DXG offer), so folding it in
// pins it ON for every dealer that has Desking Intelligence enabled. The saved-DXG pass-through path is
// therefore unreachable in production, and its integration assertion was removed rather than satisfied
// (see aiAltDealStructure.tc.ts). Re-splitting this from the dealer setting is what restores that path.
export const enableUnsavedStateForAI = (state: StateTree): boolean => isDeskingIntelligenceEnabled(state);
export const isAIVehicleRecommendationEnabled = (state: StateTree): boolean => isDeskingIntelligenceEnabled(state);
export const isAIVehicleRecommendationEnabledForLease = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableAIVehicleRecommendationForLease');
export const isAiVehicleRecommendationsChangeVehicleEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F291824_Enable_AiVehicleRecommendations_ChangeVehicle');

export const isAiAltDealStructureRecommendationsEnabled = (state: StateTree): boolean => isDeskingIntelligenceEnabled(state);
export const isEnableMVCreditAppSearchAndMerge = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableMVCreditAppSearchAndMerge');
export const isClearerSearchAndAttachExperienceEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F319966_Enable_Clearer_Search_And_Attach_Experience');
export const isEnableMVOptimizedSaveOffer = (state: StateTree): boolean => getFeatureToggle(state, 'enableMVOptimizedSaveOffer');

/** US2041815: gates persisting the compressed payment grid to the deal baggage. */
export const isPaymentGridBaggageCompressionEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, PAYMENT_GRID_BAGGAGE_COMPRESSION_TOGGLE);

export const isPaymentGridPersistenceEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F324220_Enable_Payment_Grid_Persistence');

export const isScenarioDuplicationWithAutoSelectEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableScenarioDuplicationWithAutoSelect');
export const isManagerViewQuoteModeEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'FEAT278181_MV_Quick_Quote_Mode');
export const isEnableCustomerInQuoteModeEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'FEAT304043_Enable_Customer_in_QQ_Mode');
export const isFirstPencilWorkflowEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F280192_First_Pencil_Workflow');
export const isFirstPencilSciRequestEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F319397_First_Pencil_SCI_Request');
export const isShowDealPulseInManagerViewEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'showDealPulseInManagerView');

export const isLenderFeesInAddOnsEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE769095_Include_Lender_Fees_In_Add_Ons');

export const isLenderFeesFromPricedTermEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2116905_Enable_Lender_Fees_From_Priced_Term');

export const isLenderFeesPreservedOnLeaseFallbackEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE771282_Preserve_Lender_Fees_On_Lease_Fallback');

export const isStableLenderFeesOnIncentiveReapplyEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE773854_Stable_Lender_Fees_On_Incentive_Reapply');

export const isConditionalRebateKeepGridOpenEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE774439_Enable_Conditional_Rebate_Keep_Grid_Open');

export const isOnePaySwitchCapReductionCashEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE778435_Enable_One_Pay_Switch_Cap_Reduction_Cash');

export const isNoProgramIncentiveMessageEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE771289_Enable_No_Program_Incentive_Message');

export const isNoProgramMessagePersistEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE771328_Enable_No_Program_Message_Persist');

export const isFinanceRateFallbackEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE770739_Enable_Finance_Rate_Fallback');

// Gates hiding the missing-payment alert on a deal with no vehicle, where no payment is due (DE770258).
export const isPaymentAlertVehicleAwareEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE770258_Enable_Payment_Alert_Vehicle_Aware');

export const isUnifiedResidualFieldsEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2095010_Enable_Unified_Residual_Fields');
export const isPercentTaxRatesEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2131266_Enable_Percent_Tax_Rates');

export const isEnableCrossDealershipVehicleSearchSCI = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableCrossDealershipVehicleSearchSCI');

export const isEnableCrossDealershipAIVehicleSearchMV = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableCrossDealershipAIVehicleSearchMV');

export const isEnableCrossDealershipAIVehicleSearchSCI = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableCrossDealershipAIVehicleSearchSCI');

export const isEnableCrossDealershipAIVehicleSearchSV = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableCrossDealershipAIVehicleSearchSV');

export const isEnableCrossDealershipAIVehicleSearchOD = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableCrossDealershipAIVehicleSearchOD');

export const isAgeOfInventorySearchEnabledMV = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F292323_Enable_Age_Of_Inventory_Search_MV');

export const isAgeOfInventorySearchEnabledSCI = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F292323_Enable_Age_Of_Inventory_Search_SCI');

export const isAutoExpandDealVehicleEnabledSCI = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F320928_Enable_Auto_Expand_Deal_Vehicle');

export const isTradeInAutoPayoffEnabledSCI = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F317755_tradein_auto_payoff_sci');

export const isGCDynamicValidationsEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'GCDynamicValidations');

export const isActivitiesCustomerCardEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableActivitiesCustomerCard');

export const isReportIssueEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'mvReportIssue');

export const isReportIssueV2Enabled = (state: StateTree): boolean => getFeatureToggle(state, 'mvReportIssueV2');

export const isReportIssueV2RequiredFieldsEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'mvReportIssueV2RequiredFields');

export const isEnableSoftPullConsentMV = (state: StateTree): boolean => isDeskingIntelligenceEnabled(state);

export const isEnableSoftPullConsentWithoutDLMV = (state: StateTree): boolean => isDeskingIntelligenceEnabled(state);

export const enableFSRespectTradeInclusion = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableFSRespectTradeInclusion');

export const isMVRecentDealsEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableMVRecentDeals');

export const isEnableLenderAddress = (state: StateTree): boolean => getFeatureToggle(state, 'enableLenderAddress_F267947');

export const isOPLAutoSelectLenderEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableOPLAutoSelectLender');

export const isEnableMVLenderDecisions = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableLenderDecisionFromFS_F267949');

export const isMileageOptionsUIUpdateEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US1866012_StandardAnnualMiles');

export const isResidualBreakdownEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableResidualBreakdown');

export const isFiltersProgramIncentivesEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F229388_Filters_Program_Incentives');

export const isProgramIncentivesRestorationEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableProgramIncentivesRestoration_F292564');

export const isCashDealIncentivesFixesEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2076398_Enable_Cash_Deal_Incentives_Fixes');

export const isConditionalIncentivesTotalCashDealLoadEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE771098_Use_Conditional_Incentives_Total_Cash_Deal_Load');

export const isZeroAmountIncentiveSelectionDisabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE771162_Disable_Zero_Amount_Incentive_Selection');

export const isBalloonDealTypeEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableBalloonDealType');

export const isBalloonEnhancementEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableBalloonEnhancement');

export const isDeferredShopperUrlGenerationEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableDeferredShopperUrlGeneration');

export const isRemovePSCallOnContractDateUpdateEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'removePSCallOnContractDateUpdate');

export const isCustomRateCardEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableCustomRateCard');

export const isDealerRateCardSupportEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableDealerRateCardSupport');

export const isConditionalDiscountDisplayEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableConditionalDiscountDisplay');

export const isTimeToVisualCompletionMVEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableTimeToVisualCompletionMV');

export const isMVReactProfilerEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableMVReactProfiler');

export const isCashDownPaymentFieldEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableCashDownPaymentField');
export const isAdditionalInsuranceFieldsEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableAdditionalInsuranceFields');
export const isSyncCustomerCardFieldsEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F313128_Sync_Customer_Card_Fields');
export const isShowInsuranceInUnifiedCustomerCardEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2108258_Show_Insurance_In_Unified_Customer_Card');
export const isPrivateAPIGatewayEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enablePrivateAPIGateway_F243851');
export const isPrivateApiForDealerServiceDashEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'usePrivateApiForDealerServiceDash');

export const isAllowanceSourceIndicatorEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableAllowanceSourceIndicator');

export const isEnableVehicleCostInDealRecap = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableVehicleCostInDealRecap');

export const isDealListCompareEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableVehicleSCIDealListCompare');

export const isGracefulRecalcFailureEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableGracefulRecalcFailure');

export const isTrimAndStockEditEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableTrimAndStockEdit');

export const isAllVehicleInventoryFromCVIEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2119857_Enable_All_Vehicle_Inventory_From_CVI');

export const useFetchBasedSpinnerMV = (state: StateTree): boolean => getFeatureToggle(state, 'useFetchBasedSpinnerMV');

export const disableCCIDSessionFallback = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE741887_Disable_CCID_Session_Fallback');

export const enableStaleDraftVersionDiscard = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableStaleDraftVersionDiscard');

export const isHideChangeVehicleNoDealershipsEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableHideChangeVehicleNoDealerships');

export const isDMSExportStatusDisplayEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableDMSExportStatusDisplay');

export const isTradeEvaluationReconCostMVPrintingEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableTradeEvaluationReconCostMVPrinting');

export const isLenderProgramNamesRnREnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableLenderProgramNamesRnR');

export const isFuelSavingAnalysisEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableFuelSavingAnalysis');

export const isEnableAutoSavingInFuelSaving = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableAutoSavingInFuelSaving');

export const isCreditAppFieldsCoverageEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableCreditAppFieldsCoverage');

export const isCreditAppConsentEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F311457_Enable_Credit_Consent');

export const isPreserveCrmTradeVehicleInfoEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2078183_Preserve_Crm_Trade_Vehicle_Info');

export const isEnableDealerPreferredBookSource = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableDealerPreferredBookSource');

export const isEnhancedShowroomModeEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableEnhancedShowroomMode');

export const isCTPropertyTaxCalculationEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableCTPropertyTaxCalculation');

export const isLeaseResidualTreatmentEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableLeaseResidualTreatment_F286227');

export const isImprovedAutoSelectEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F283308_Enable_Improved_Auto_Select');

/**
 * Gates the MRM (Max Residualized MSRP) override indicator. F283308's own AC-5/F6 lists MRM among the
 * lender-program fields the indicator must cover, but none of that feature's delivered bundles wired it
 * up (DE771374). A separate toggle, rather than folding MRM into `isImprovedAutoSelectEnabled`, lets this
 * fix roll out independently of that feature's own rollout state.
 */
export const isMrmOverrideIndicatorEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE771374_Enable_MRM_Override_Indicator');

export const isPgOverrideIndicatorEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2139463_PG_Override_Indicator');

export const isPaymentFrequencyTermMessageEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE771161_Enable_Payment_Frequency_Term_Message');

export const isSpotMsgNormalizationEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableSpotMsgNormalization');

export const enableCdmsSyncFlow = (state: StateTree): boolean => getFeatureToggle(state, 'enableCdmsSyncFlow');

export const isLeaseResidualPqFirstEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableLeaseResidualPqFirst_F286227');

export const isLogicalDraftRetentionEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableLogicalDraftRetention');

export const enableLenderAddress = (state: StateTree): boolean => getFeatureToggle(state, 'enableLenderAddress');

export const isAutoSelectContentsOnClickEnabledAfterBootstrap = (state: StateTree): boolean =>
    getFeatureToggleFromSlice(getTogglesWithoutSafetyChecks(state), 'DE753030_Auto_Select_Contents_On_Click');

export const isAutoSelectContentOnSlideOutsEnabledAfterBootstrap = (state: StateTree): boolean =>
    getFeatureToggleFromSlice(getTogglesWithoutSafetyChecks(state), 'US2081831_Auto_Select_Content_On_Click_Slideouts');

export const isCarsActDisclosuresEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F285639_Enable_Cars_Act_Disclosures');

export const isLoosenedCtaValidationEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2123397_Enable_Loosened_Cta_Validation');

export const isMathStackEvaluationEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F318449_Enable_Math_Stack_Warning_Alert');

export const isSciCustomerCardPreloaderEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2107090_Enable_SCI_Customer_Card_Preloader');

/**
 * Gates the global payment sequence guard (`dash-store/middleware/paymentSequenceMiddleware`).
 *
 * IMPORTANT: callers must check `areFeatureTogglesAvailable(state)` first. This selector runs
 * `featureTogglePreconditionCheck`, which throws pre-bootstrap, and payment actions can dispatch
 * before the toggle slice is hydrated.
 */
export const isPaymentSequenceGuardEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F303836_Enable_Payment_Sequence_Guard');

/**
 * Gates `guardStaleTermIncentiveWrites` in `paymentSequenceMiddleware.ts` — the term-level splice that
 * restores incentive/rebate fields on a stale response. Deliberately a SEPARATE toggle from
 * `isPaymentSequenceGuardEnabled` rather than folded into it: unlike the lane guard, which only ever
 * refuses to forward (all-or-nothing), this one rewrites a payload before forwarding it, a different
 * risk profile that needs its own independent kill switch. Checked in addition to, not instead of, the
 * lane guard's toggle — this one is a no-op if the lane guard itself is off.
 *
 * IMPORTANT: callers must check `areFeatureTogglesAvailable(state)` first, same as
 * `isPaymentSequenceGuardEnabled` above.
 */
export const isTermIncentiveGuardEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE767001_Enable_Term_Incentive_Guard');
/**
 * DE785100: a grid-row response older than one already applied to the same term keeps the newer term whole,
 * instead of overwriting the cell. Independent of DE767001. Callers check `areFeatureTogglesAvailable` first.
 */
export const isGridRowStaleTermGuardEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE785100_Enable_Grid_Row_Stale_Term_Guard');
export const isTermFieldDownPaymentTermIdEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE771128_Enable_Term_Field_Down_Payment_Term_Id');
export const isAppliedIncentiveDisplayFixesEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE765882_Enable_Applied_Incentive_Display_Fixes');
export const isResponseAppliedIncentiveAmountEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE778704_Enable_Response_Applied_Incentive_Amount');
export const isCashOfferPriceFallbackEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE783842_Enable_Cash_Offer_Price_Fallback');
export const isGridCellCashDownMatchEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE784548_Enable_Grid_Cell_Cash_Down_Match');
export const isRollToApplyIncentiveCatalogEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE779915_Enable_Roll_To_Apply_Incentive_Catalog');
export const isKeepLenderOnZipChangeEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE778958_Enable_Keep_Lender_On_Zip_Change');
export const isNewScenarioVehicleSwitchEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE782209_Enable_New_Scenario_Vehicle_Switch');
export const isDasPaymentRefreshEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE783156_Enable_Das_Payment_Refresh');
export const isEditedProgramIncentiveDedupeEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE777671_Enable_Edited_Program_Incentive_Dedupe');
export const isKeepUnnamedManualRebateEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE774159_Enable_Keep_Unnamed_Manual_Rebate');
export const isUpqRebateLedgerEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'DE771128_Enable_UPQ_Rebate_Ledger');
export const isRequoteAfterGuardedVehicleSwitchEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE778976_Enable_Requote_After_Guarded_Vehicle_Switch');
export const isRetainedProgramLatchEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE773156_Enable_Retained_Program_Latch');
export const isProgramsMilesYearConsistencyEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE773452_Enable_Programs_Miles_Year_Consistency');
export const isMultiScenarioGridConsistencyEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE774740_Enable_Multi_Scenario_Grid_Consistency');
export const isTemplateRateUpqLockEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F325266_Enable_Template_Rate_Upq_Lock');
export const isBuyRatePqFirstEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'DE773860_Enable_Buy_Rate_PQ_First');
export const isAdditionalSalesRolesEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableAdditionalSalesRoles');
export const isDontClobberSalesManagerOnSaveEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE767857_Dont_Clobber_Sales_Manager_On_Save');
export const isUseGridHideComparisonEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE771341_Use_Grid_Hide_Comparison');
export const isCviLoadRebaselineEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE774211_Enable_Cvi_Load_Rebaseline');
export const isLoadEnrichmentRebaselineEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE774211_Enable_Load_Enrichment_Rebaseline');
export const isAspPaymentGridRedesignEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F298789_ASP_PG_redesign');
export const isExplicitLenderP1Enabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableExplicitLenderP1');
export const isAutoSelectedLenderProgramBannerEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2149134_Show_Auto_Selected_Lender_Program_Banner');

export const isRoAlertInMVEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F227301_Enable_RO_Alert_in_MV');

export const isInlineTaxedIndicatorEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'enableInlineTaxedIndicator');

export const isCommitActiveFieldOnSaveEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE759192_Commit_Active_Field_On_Save');

/**
 * Escape hatch for the DE759192 `usePaymentCall` fix: ON skips the premature `isPaymentCallPending` clear
 * on blur/Enter (the fix, letting the real payment response clear it); OFF preserves today's behavior so
 * it can be flipped back in seconds without a redeploy if a `usePaymentCall` consumer misbehaves.
 */
export const isSkipPrematurePaymentPendingClearEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE759192_Skip_Premature_Payment_Pending_Clear');

export const isMVVppOverrideIndicatorEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'enableMVVppOverrideIndicator');

export const isVehiclePrimaryUseEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F289631_Enable_Primary_Use');

/** US2038890. Distinct from `F289631_Enable_Primary_Use`, which only gates the field itself. */
export const isUsageAndCustomerTypeSurchargesEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F289631_Enable_Usage_And_Customer_Type_Surcharges');

export const enableRightToCancelFields = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F310030_Enable_Right_To_Cancel_Fields');

export const enablePromptOptimization = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F287702_Enable_Prompt_Optimization');

export const isSimilarityBodyStyleRelaxationEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE783583_Enable_Similarity_BodyStyle_Relaxation');

/**
 * Gates the consolidated Customer Card save (see `docs/dep/US2067968-ConsolidatedParticipantSave.md`).
 *
 * ON: Apply issues a single `saveCustomerCard` request (participants + SPD + insurance in one read/write).
 * OFF: Apply keeps the three parallel saves. The BFF routes on this toggle, not on the payload shape.
 */
export const isCustomerCardSaveConsolidationEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F289106_Enable_Customer_Card_Save_Consolidation');

export const isFsSyncFailureMessagingEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F295385_UI_Messaging_for_MV_DT');

export const isSwapBuyerCoBuyerEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F243783_Enable_Swap_Buyer_CoBuyer');

export const isChangeBuyerCoBuyerEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F243783_Enable_Change_Buyer_CoBuyer');

export const isDropCoBuyerInCardEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F243783_Enable_Drop_CoBuyer_In_Card');

export const isCreditAppUpdateOnChangeBuyerEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F243783_Enable_Credit_App_Update_On_Change_Buyer');

export const isEcommerceCancelOnBuyerEmailChangeEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F243783_Enable_Ecommerce_Cancel_On_Buyer_Email_Change');

/**
 * Switches Quick Quote's existing Add Customer action from the Common Consumer drawer to the customer
 * card's own search. `Use_` rather than `Enable_` because the action and its quote-mode gating belong
 * to F304043 either way — only the implementation behind it changes.
 */
export const isNativeAddCustomerEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F243783_Use_Native_Add_Customer');

export const isNativeAddCoBuyerEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F243783_Use_Native_Add_CoBuyer');

/**
 * Render the VinSolutions `customer` remotes as Federated Islands (their own React root, bound to an
 * isolated share scope) instead of `lazy(loadRemoteModule(...))`.
 *
 * OFF is the legacy path and is correct while VinSolutions is still on React 18. It must be turned ON
 * before their React 19 rollout, because a React 19 element rendered inside this React 18 tree throws
 * `Minified React error #31`. See US2082927 and `createRemoteModule` in dr-shared-components.
 *
 * Deliberately the non-throwing variant. `createRemoteModule` reads this on every render of the
 * customer modules, and the throwing form would introduce a crash where the legacy path — which read
 * no toggle at all — had none. Reading `false` before bootstrap yields the legacy path, which is
 * exactly the pre-toggle behaviour, so the unsafe default is also the safe one here.
 */
export const isFederatedIslandsEnabledAfterBootstrap = (state: StateTree): boolean =>
    getTogglesWithoutSafetyChecks(state).US2082927_Enable_Federated_Islands || false;

export const isPIScreenEnhancementsEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F311594_Enable_PnIScreen_Enhancements');

export const isPaperLenderEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F286514_Enable_Paper_Lender');

/**
 * NOTE: reads without the bootstrap precondition check, despite the plain `isXyzEnabled` name.
 * `navigationMiddleware` reads this on every `SET_WORKFLOW_STATUS`, including `IDLE` (bootstrap not
 * yet returned) and `BOOTSTRAP_ERROR` (bootstrap failed) — a precondition-checked read would throw
 * there and turn a handled bootstrap failure into an unhandled one. Reading it as `false` before
 * bootstrap is safe: both workflow route maps agree on every status that has no section.
 */
export const isSciFullPageScrollEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F304636_Enable_Full_Page_Scroll');

export const isLeaseReduxTelemetryEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F316138_Enable_Lease_Redux_Telemetry');

export const isCashReduxTelemetryEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2140882_Enable_Cash_Redux_Telemetry');

export const isMultiScenarioOfferEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F319548_Show_Multi_Scenarios_With_Payment_Details');
export const isShowMvOfferCommentsInSalesPresentationEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2142676_Show_MV_Offer_Comments_In_Sales_Presentation');
export const isMvRedesignEnabled = (state: StateTree): boolean => getFeatureToggle(state, 'F321525_Enable_MV_Redesign');

export const isDealScopedNetTradeEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE769035_Enable_Deal_Scoped_Net_Trade');

export const isSkipDarwinProfitRecalcEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE774699_Skip_Darwin_Profit_Recalc');

export const isBookoutErrorsOnOpenEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE774273_Show_Bookout_Errors_On_Open');

export const isTradeInIntelligenceEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F284441_Enable_Trade_In_Intelligence');

export const isProcessSciDtUpdatesEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE770962_Process_SCI_DT_Updates');

export const isStickyFiltersAndSortingEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F321993_Enable_StickyFiltersAndSorting');

export const isFeesSlideoutRowStabilityEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2164415_Enable_FeesSlideoutRowStability');

export const isPreserveFieldsOnPaymentResponseEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE779787_Preserve_Fields_On_Payment_Response');

export const isRollToUpqOnFirstCalculationEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE783996_Enable_Roll_To_UPQ_On_First_Calculation');
export const isSecurityDepositOverrideResetEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE784015_Enable_Security_Deposit_Override_Reset');

export const isNegativeLeaseQuoteRejectionEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE783159_Enable_Negative_Lease_Quote_Rejection');

export const isRateOverrideSupersedeGuardEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE784527_Enable_Rate_Override_Supersede_Guard');

export const isVppProductChangeSupersedeGuardEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE783828_Enable_Vpp_Product_Change_Supersede_Guard');

export const isNetCashPayoutPersistenceEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F320295_Enable_NetCashPayout_Persistence');

export const isCviInvoiceMismatchAlertEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F321380_Enable_Cvi_Invoice_Mismatch_Alert');

export const isOnePayLeaseFirstPaymentEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'DE781175_One_Pay_Lease_First_Payment');

export const isPostalCodeDigitsOnlyValidationEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2125037_MV_Postal_Code_Digits_Only_Validation');

export const isDealerTimeZoneDisclaimerEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'US2157264_Enable_Dealer_Time_Zone_Disclaimer');

export const isDealAssistantChatEnabled = (state: StateTree): boolean =>
    getFeatureToggle(state, 'F266723_Enable_Deal_Assistant_Chat');
